import type { Chat } from '@ai-sdk/react';
import type { CrowdexUIMessage } from '@crowdex/agent-contracts';
import type { ExecutionMode } from '@/components/worktree-controls';
import type { CloudRuntime } from '@/lib/cloud-runtime/cloud-runtime';
import {
  type ConversationState,
  conversationStateOf,
  type DesktopShellState,
  type DesktopShellStore,
} from '@/lib/desktop-shell/store';
import { WORKSPACE_KEY } from '@/lib/desktop-storage-keys';

/** The chats of the conversations opened in this window, by conversation id. */
export type ConversationChats = Map<string, Chat<CrowdexUIMessage>>;

/** Where a conversation runs: its folder, its runtime and its agent. */
export type ConversationPlace = {
  workspace?: string | null;
  executionMode?: ExecutionMode;
  agentId?: string;
};

type MessagesUpdate = CrowdexUIMessage[] | ((current: CrowdexUIMessage[]) => CrowdexUIMessage[]);

/**
 * One conversation's own handle. Work started for a conversation (a worktree, a cloud sandbox, a
 * branch switch, a reply) keeps it and settles through it: the user may have opened another
 * conversation meanwhile, and the shell's fields then describe that other one.
 */
export type ConversationScope = {
  readonly chatId: string;
  /** Whether it is still the conversation the shell shows. */
  shown: () => boolean;
  /**
   * Runs `write` on the shell state only while this conversation is the one shown, and says
   * whether it ran: the shell's fields describe the shown conversation, never another's.
   */
  whileShown: (write: (state: DesktopShellState) => void) => boolean;
  /** What it is going through now (its waiting message, its worktree being made…). */
  state: () => ConversationState;
  /** Changes what it is going through, shown or not (`ConversationState`). */
  update: (
    update:
      | Partial<ConversationState>
      | ((current: ConversationState) => Partial<ConversationState>),
  ) => void;
  /**
   * Moves the conversation (its folder, runtime or agent): the shell follows while it is shown,
   * and its cached copy, which it is restored from when shown again, either way.
   */
  place: (place: ConversationPlace) => void;
  /** Its transcript: its chat's, else its cached copy's. */
  messages: () => readonly CrowdexUIMessage[];
  /** Changes its transcript wherever it is, never the one shown in its place. */
  setMessages: (update: MessagesUpdate) => void;
  /** Where its files are: its folder, and the cloud runtime holding it while it is shown. */
  files: () => { root: string | null; cloudRuntime: CloudRuntime | null };
};

export type ConversationScopes = {
  of: (chatId: string) => ConversationScope;
  /** The conversation shown now, held by its id: what settles later still lands in it. */
  shown: () => ConversationScope;
};

/**
 * Runs `write` on the shell state only while `chatId` is the conversation shown, and says whether
 * it ran (`ConversationScope.whileShown`, for code that holds no scope).
 */
export function whileConversationShown(
  store: DesktopShellStore,
  chatId: string,
  write: (state: DesktopShellState) => void,
): boolean {
  const state = store.getState();
  if (state.chatId !== chatId) return false;
  write(state);
  return true;
}

/** The one way the desktop shell writes what belongs to a conversation (see `ConversationScope`). */
export function createConversationScopes(
  store: DesktopShellStore,
  chats: ConversationChats,
): ConversationScopes {
  function of(chatId: string): ConversationScope {
    const shown = () => store.getState().chatId === chatId;

    const whileShown = (write: (state: DesktopShellState) => void) =>
      whileConversationShown(store, chatId, write);

    function place(next: ConversationPlace): void {
      whileShown((state) => {
        const mode = next.executionMode ?? state.executionMode;
        if (next.workspace !== undefined) {
          // The last folder opened on this machine is where the next conversation starts.
          if (next.workspace !== null && mode !== 'cloud')
            localStorage.setItem(WORKSPACE_KEY, next.workspace);
          state.setWorkspace(next.workspace);
        }
        if (next.executionMode !== undefined) state.setExecutionMode(next.executionMode);
        if (next.agentId !== undefined) state.setAgentId(next.agentId);
      });
      store.getState().setConversationCache((cache) => {
        const cached = cache[chatId];
        if (cached === undefined) return cache;
        return {
          ...cache,
          [chatId]: {
            ...cached,
            ...(next.workspace === undefined ? {} : { workspaceRoot: next.workspace }),
            ...(next.executionMode === undefined ? {} : { runtime: next.executionMode }),
            ...(next.agentId === undefined ? {} : { agentId: next.agentId }),
          },
        };
      });
    }

    function messages(): readonly CrowdexUIMessage[] {
      return (
        chats.get(chatId)?.messages ?? store.getState().conversationCache[chatId]?.messages ?? []
      );
    }

    function setMessages(update: MessagesUpdate): void {
      const chat = chats.get(chatId);
      const current = chat?.messages ?? store.getState().conversationCache[chatId]?.messages;
      if (current === undefined) return;
      const next = typeof update === 'function' ? update(current) : update;
      if (chat !== undefined) chat.messages = next;
      // The shown one's copy follows its chat (`useConversationPersistence`); another's is
      // what its split pane and the sidebar show.
      if (shown()) return;
      store.getState().setConversationCache((cache) => {
        const cached = cache[chatId];
        if (cached === undefined) return cache;
        return { ...cache, [chatId]: { ...cached, messages: next, updatedAt: Date.now() } };
      });
    }

    function files(): { root: string | null; cloudRuntime: CloudRuntime | null } {
      const state = store.getState();
      if (state.chatId === chatId)
        return { root: state.workspace, cloudRuntime: state.cloudRuntime };
      const cached = state.conversationCache[chatId];
      // A cloud conversation's files are reached through its runtime, connected once it shows.
      if (cached === undefined || cached.runtime === 'cloud')
        return { root: null, cloudRuntime: null };
      return { root: cached.workspaceRoot, cloudRuntime: null };
    }

    return {
      chatId,
      shown,
      whileShown,
      state: () => conversationStateOf(store.getState(), chatId),
      update: (update) => store.getState().setConversationState(chatId, update),
      place,
      messages,
      setMessages,
      files,
    };
  }

  return { of, shown: () => of(store.getState().chatId) };
}
